"use client";

import type { UIMessage } from "ai";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  computeJourneySections,
  type JourneySectionConfig,
  type JourneySectionProgress,
  type SpecField,
} from "./journey-sections";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface JourneyState {
  /** Whether the profile panel is open. */
  isOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;

  /** Section progress derived from message history. */
  sections: JourneySectionProgress[];
  /** LLM synthesis from show_patient_profile.synthesis. */
  synthesis: string | null;

  /** Brief pulse flag for header animation — true for ~2s after progress updates. */
  pulseHeader: boolean;

  /** Convenience: at least one section is complete. */
  hasAnySectionComplete: boolean;
  /** ID of the most recently completed section (null if none). */
  lastCompletedSection: string | null;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const JourneyContext = createContext<JourneyState | null>(null);

/**
 * useJourney — access journey state from any component inside the provider tree.
 * Returns null when no JourneyProvider is present (graceful degradation for
 * non-journey-enabled variants).
 */
export function useJourney(): JourneyState | null {
  return useContext(JourneyContext);
}

// ---------------------------------------------------------------------------
// Message scanning utilities
// ---------------------------------------------------------------------------

interface ToolPart {
  toolName: string;
  args: Record<string, unknown>;
  result?: unknown;
}

/** Extract tool invocation parts from a UIMessage (AI SDK v6 shape). */
function extractToolParts(message: UIMessage): ToolPart[] {
  if (message.role !== "assistant") return [];
  const parts: ToolPart[] = [];
  for (const part of message.parts ?? []) {
    // AI SDK v6: tool parts have type "tool-<toolName>"
    if (part.type.startsWith("tool-")) {
      const toolPart = part as {
        type: string;
        state: string;
        input?: Record<string, unknown>;
        output?: unknown;
      };
      parts.push({
        toolName: toolPart.type.replace("tool-", ""),
        args: (toolPart.input ?? {}) as Record<string, unknown>,
        result: toolPart.state === "output-available" ? toolPart.output : undefined,
      });
    }
  }
  return parts;
}

/**
 * Build the canonical answered-fields map from the full message history.
 *
 * Handles:
 * - save_intake_fields → upsert all fields
 * - intake_edit_field → overwrite field
 * - intake_go_back → clear fields for the returned-to group
 */
function buildAnsweredMap(messages: UIMessage[], specFields: SpecField[]): Record<string, unknown> {
  const answered: Record<string, unknown> = {};

  // Build group membership index for intake_go_back
  const fieldsByGroup = new Map<string, string[]>();
  for (const field of specFields) {
    if (field.group) {
      const existing = fieldsByGroup.get(field.group) ?? [];
      existing.push(field.id);
      fieldsByGroup.set(field.group, existing);
    }
  }

  for (const message of messages) {
    for (const tool of extractToolParts(message)) {
      switch (tool.toolName) {
        case "save_intake_fields": {
          const { fields } = tool.args as {
            fields?: Array<{ field?: string; fieldId: string; value: unknown }>;
          };
          if (fields) {
            for (const f of fields) {
              const id = f.fieldId || f.field;
              if (id && f.value !== undefined) {
                answered[id] = f.value;
              }
            }
          }
          break;
        }
        case "intake_edit_field": {
          const { field, fieldId, value } = tool.args as {
            field?: string;
            fieldId?: string;
            value?: unknown;
          };
          const id = fieldId || field;
          if (id && value !== undefined) {
            answered[id] = value;
          }
          break;
        }
        case "intake_go_back": {
          const { group } = tool.args as { group?: string };
          if (group) {
            const groupFields = fieldsByGroup.get(group) ?? [];
            for (const fid of groupFields) {
              delete answered[fid];
            }
          }
          break;
        }
      }
    }
  }

  return answered;
}

/** Extract the latest show_patient_profile synthesis from messages. */
function extractSynthesis(messages: UIMessage[]): string | null {
  let synthesis: string | null = null;
  for (const message of messages) {
    for (const tool of extractToolParts(message)) {
      if (tool.toolName === "show_patient_profile") {
        const s = (tool.args as Record<string, unknown>).synthesis;
        if (typeof s === "string" && s.length > 0) {
          synthesis = s;
        }
      }
    }
  }
  return synthesis;
}

// ---------------------------------------------------------------------------
// Provider component
// ---------------------------------------------------------------------------

export interface JourneyProviderProps {
  /** Full conversation history (from ChatPanel). */
  messages: UIMessage[];
  /** Journey section config from variant's journey_sections. */
  config: JourneySectionConfig[];
  /** Spec fields (from experience spec). Needed for group membership + summaries. */
  specFields: SpecField[];
  /** Pre-collected fields from patient model (DB). Hydrates progress on page reload. */
  initialAnswered?: Record<string, unknown>;
  children: React.ReactNode;
}

export function JourneyProvider({
  messages,
  config,
  specFields,
  initialAnswered,
  children,
}: JourneyProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pulseHeader, setPulseHeader] = useState(false);
  const [lastCompletedSection, setLastCompletedSection] = useState<string | null>(null);
  const pulseTimeoutRef = useRef<number | null>(null);
  const completionTimeoutRef = useRef<number | null>(null);
  const prevSectionsRef = useRef<JourneySectionProgress[]>([]);

  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);

  // Compute progress: merge DB-persisted fields with message-derived answers.
  // Message-derived answers take precedence (they reflect edits/back-navigation).
  const answered = useMemo(() => {
    const fromMessages = buildAnsweredMap(messages, specFields);
    if (!initialAnswered) return fromMessages;
    return { ...initialAnswered, ...fromMessages };
  }, [messages, specFields, initialAnswered]);
  const sections = useMemo(
    () => computeJourneySections(config, specFields, answered),
    [config, specFields, answered],
  );
  const synthesis = useMemo(() => extractSynthesis(messages), [messages]);

  // Detect section completion changes → pulse header + transient lastCompletedSection
  useEffect(() => {
    const prev = prevSectionsRef.current;
    const newlyCompletedSection = sections.find(
      (s) => s.status === "complete" && prev.find((p) => p.id === s.id)?.status !== "complete",
    );
    const progressChanged = sections.some((s) => {
      const p = prev.find((p) => p.id === s.id);
      return p && s.fieldsCollected > p.fieldsCollected;
    });

    if (newlyCompletedSection) {
      setLastCompletedSection(newlyCompletedSection.id);
      if (completionTimeoutRef.current) window.clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = window.setTimeout(() => setLastCompletedSection(null), 3000);
    }

    if (newlyCompletedSection || progressChanged) {
      setPulseHeader(true);
      if (pulseTimeoutRef.current) window.clearTimeout(pulseTimeoutRef.current);
      pulseTimeoutRef.current = window.setTimeout(() => setPulseHeader(false), 2000);
    }
    prevSectionsRef.current = sections;
  }, [sections]);

  // Listen for cross-tree "journey:open-panel" event (e.g., from AuthHeader outside provider)
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener("journey:open-panel", handler);
    return () => window.removeEventListener("journey:open-panel", handler);
  }, []);

  // Cleanup timeouts on unmount
  useEffect(
    () => () => {
      if (pulseTimeoutRef.current) window.clearTimeout(pulseTimeoutRef.current);
      if (completionTimeoutRef.current) window.clearTimeout(completionTimeoutRef.current);
    },
    [],
  );

  const hasAnySectionComplete = sections.some((s) => s.status === "complete");

  const value: JourneyState = useMemo(
    () => ({
      isOpen,
      openPanel,
      closePanel,
      sections,
      synthesis,
      pulseHeader,
      hasAnySectionComplete,
      lastCompletedSection,
    }),
    [
      isOpen,
      openPanel,
      closePanel,
      sections,
      synthesis,
      pulseHeader,
      hasAnySectionComplete,
      lastCompletedSection,
    ],
  );

  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
}
